<template>
  <div :class="classes">
    <label v-if="label" :class="labelClasses" :style="labelStyle">
      <slot name="label">{{ label }}</slot>
    </label>
    <div :class="contentClasses">
      <slot></slot>
      <transition name="fade">
        <div v-if="validateState === 'error' && showMessage && form.showMessage" class="form-feedback">{{ validateMessage }}</div>
      </transition>
    </div>
  </div>
</template>
<script>
import AsyncValidator from 'async-validator'
import Emitter from '../../mixins/emitter'
const PREFIXCLS = 'form-group'
function getPropByPath(obj, path) {
  let tempObj = obj
  path = path.replace(/\[(\w+)\]/g, '.$1')
  path = path.replace(/^\./, '')
  let keyArr = path.split('.')
  let i = 0
  for (let len = keyArr.length; i < len - 1; ++i) {
    let key = keyArr[i]
    if (key in tempObj) {
      tempObj = tempObj[key]
    } else {
      throw new Error(
        '[taurus warn]: please transfer a valid prop path to form item!'
      )
    }
  }
  return {
    o: tempObj,
    k: keyArr[i],
    v: tempObj[keyArr[i]]
  }
}
export default {
  name: 'TFormItem',
  mixins: [Emitter],
  props: {
    label: {
      type: String,
      default: ''
    },
    labelSpan: {
      type: [Number, String],
      default: ''
    },
    prop: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: [Object, Array],
      default: () => {}
    },
    error: {
      type: String,
      default: ''
    },
    validateStatus: {
      type: Boolean
    },
    showMessage: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isRequired: false,
      validateState: '',
      validateMessage: '',
      validateDisabled: false,
      validator: {},
      formRules: {}
    }
  },
  computed: {
    classes() {
      return [
        `${PREFIXCLS}`,
        {
          [`${PREFIXCLS}--required`]: this.required || this.isRequired,
          'has-danger': this.validateState === 'error',
          'row': this.form.labelPosition !== 'top',
          [`${PREFIXCLS}--validating`]: this.validateState === 'validating'
        }
      ]
    },
    labelStyle() {
      if (this.form.labelPosition !== 'top') {
        if (this.labelSpan && typeof this.labelSpan === 'string') {
          return `width:${this.labelSpan}`
        }
        if (typeof this.form.labelSpan === 'string') {
          return `width:${this.form.labelSpan}`
        }
      }
      return ''
    },
    labelClasses() {
      let labelSpanType = typeof this.labelSpan
      let classes = [
        `${PREFIXCLS}__label`
      ]
      if (this.form.labelPosition !== 'top') {
        if (labelSpanType === 'number') {
          classes.push(`col-${this.labelSpan}`)
        } else if (!this.labelSpan && (typeof this.form.labelSpan === 'number')) {
          classes.push(`col-${this.form.labelSpan}`)
        }
      }
      return classes
    },
    contentClasses() {
      return [
        `${PREFIXCLS}__content`,
        {
          'col': this.form.labelPosition !== 'top'
        }
      ]
    },
    form() {
      let parent = this.$parent
      while (parent.$options.name !== 'TForm') {
        parent = parent.$parent
      }
      return parent
    },
    fieldValue: {
      cache: false,
      get() {
        const MODEL = this.form.model
        if (!MODEL || !this.prop) {
          return
        }
        let path = this.prop
        if (path.indexOf(':') !== -1) {
          path = path.replace(/:/, '.')
        }
        return getPropByPath(MODEL, path).v
      }
    }
  },
  watch: {
    error(val) {
      this.validateMessage = val
      this.validateState = 'error'
    },
    validateStatus(val) {
      this.validateState = val
    }
  },
  mounted() {
    if (this.prop) {
      // this.dispatch('TForm', 'on-form-item-add', this)
      this.form.$emit('g-on-form-item-add', this)
      Object.defineProperty(this, 'initialValue', {
        value: this.fieldValue
      })
      let rules = this.$_getRules()
      if (rules.length) {
        rules.every(rule => {
          if (rule.required) {
            this.isRequired = true
          } else {
            this.isRequired = false
          }
        })
        // this.$on('on-form-blur', this.$_onFieldBlur)
        // this.$on('on-form-change', this.$_onFieldChange)
        this.$on('g-on-form-change', this.$_onFieldChange)
        this.$on('g-on-form-blur', this.$_onFieldBlur)
      }
    }
    this.form.$on('rules-change', (val) => {
      localStorage.setItem('time', new Date())
      if (val) {
        let rules = this.$_getRules()
        if (rules.length) {
          rules.every(rule => {
            if (rule.required) {
              this.isRequired = true
              return false
            } else {
              this.isRequired = false
            }
          })
          this.$on('g-on-form-change', this.$_onFieldChange)
          this.$on('g-on-form-blur', this.$_onFieldBlur)
        }
      }
    })
  },
  beforeDestroy() {
    this.form.$emit('g-on-form-item-remove', this)
    // this.dispatch('TForm', 'on-form-item-remove', this)
  },
  methods: {
    $_getRules() {
      let formRules = this.form.rules
      const SELFRULES = this.rules
      formRules = formRules ? formRules[this.prop] : []
      return [].concat(SELFRULES || formRules || [])
    },
    $_getFilteredRule(trigger) {
      const RULES = this.$_getRules()
      return RULES.filter(
        rule => !rule.trigger || rule.trigger.indexOf(trigger) !== -1
      )
    },
    validate(trigger, callback = function() {}) {
      const RULES = this.$_getFilteredRule(trigger)
      if (!RULES || RULES.length === 0) {
        callback()
        return true
      }
      this.validateState = 'validating'
      let descriptor = {}
      descriptor[this.prop] = RULES
      const VALIDATOR = new AsyncValidator(descriptor)
      let model = {}
      model[this.prop] = this.fieldValue
      VALIDATOR.validate(model, { firstFields: true }, errors => {
        this.validateState = !errors ? 'success' : 'error'
        this.validateMessage = errors ? errors[0].message : ''
        callback(this.validateMessage)
      })
    },
    resetField() {
      this.validateState = ''
      this.validateMessage = ''
      let model = this.form.model
      let value = this.fieldValue
      let path = this.prop
      if (path.indexOf(':') !== -1) {
        path = path.replace(/:/, '.')
      }
      let prop = getPropByPath(model, path)
      if (Array.isArray(value)) {
        // this.validateDisabled = true
        prop.o[prop.k] = [].concat(this.initialValue)
      } else {
        // this.validateDisabled = false
        prop.o[prop.k] = this.initialValue
      }
    },
    $_onFieldBlur(value, prop) {
      this.validate('blur')
    },
    $_onFieldChange(value, prop) {
      // if (this.validateDisabled) {
      //   this.validateDisabled = false
      //   return
      // }
      if (this.prop) {
        this.validate('change')
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/form/form-item.vue